import { minNoiseFloorRms } from '../vocal/constants.js'
import type { PitchFrame, SungNote } from '../vocal/types.js'
import type { RawNote } from './quantize.js'

/**
 * Segments from `VocalLib.segmentNotes` become raw notes.
 *
 * The segmenter is the Pitch Trainer's, unchanged: it already knows about the onset scoop, the
 * 50-cent boundary and the whole-vibrato-period centre, and re-deciding any of that here would be a
 * second opinion about singing that this file has no business having (spec §9 - Bowie owns it). All
 * this adds is the two things a melody needs and a `SungNote` does not carry: a rounded MIDI number
 * and a velocity.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

/**
 * Velocity is RELATIVE WITHIN THE TAKE - Bowie's §8 ruling, 2026-09-09.
 *
 * The first build mapped an absolute window, `[noise floor + 12 dB, -6 dBFS] -> [0.3, 1.0]`. That
 * hands the melody's dynamics to the microphone gain: a softly hummed take comes back uniformly
 * near the floor and a close-miked one uniformly near full, and neither is what the singer
 * phrased. The same performance recorded twice at different input levels produced two different
 * melodies.
 *
 * So the scale is set by the take itself: the quietest note in it anchors the floor, the loudest
 * anchors full, and everything between is interpolated. Scaling the whole recording up or down
 * moves `lo` and `hi` together and leaves every velocity unchanged, which is the property this
 * rule exists for.
 *
 * The voicing gate is untouched and still upstream: `noise floor + 12 dB` decides what counts as a
 * note at all (`VocalLib.voicedFrames`). This function only ranks the notes that survived it.
 */

/**
 * The quietest note in a take with dynamics.
 *
 * Not zero and not near it: the quietest thing the singer actually sang is still a real note, and
 * a melody note that plays back as a ghost misrepresents the take as much as a missing one would.
 * 0.4 is audible on every soundfont while leaving clear room below the loudest note.
 */
export const CAPTURE_VELOCITY_FLOOR = 0.4

/**
 * A take whose peaks all sit inside this many dB has no dynamics to preserve.
 *
 * 6 dB is roughly the spread a listener hears as a deliberate change in dynamic rather than as the
 * ordinary unevenness of consecutive notes. Below it, stretching the range would invent a crescendo
 * out of measurement noise - and it would do so most violently on the takes where it is least
 * warranted, since a 1 dB span would be blown up to the full velocity range.
 */
export const FLAT_DYNAMICS_DB = 6

/**
 * What every note gets when the take has no dynamics to read - and what a note whose level could
 * not be measured gets.
 *
 * Mezzo-forte: the honest answer when the recording says nothing about how hard a note was played.
 * The same value the MIDI path uses for a device that reports no attack.
 */
export const FLAT_TAKE_VELOCITY = 0.8

export interface SegmentsToRawOptions {
  /** The frames the segments were derived from - the only place a level can be read. */
  frames: PitchFrame[]
  /** The click track's beat 1, ms on the frame clock. Defaults to the start of the window. */
  t0Ms?: number
}

const toDb = (rms: number) => 20 * Math.log10(Math.max(rms, minNoiseFloorRms))

export const segmentsToRawNotes = (segments: SungNote[], options: SegmentsToRawOptions): RawNote[] => {
  const { frames, t0Ms = 0 } = options

  /** Each segment's loudest frame - the note's own level. `null` when no frame fell inside it. */
  const peaks = segments.map(segment => {
    const inSegment = frames.filter(frame => frame.time >= segment.startTime && frame.time <= segment.endTime)
    return inSegment.length === 0 ? null : inSegment.reduce((loudest, frame) => Math.max(loudest, frame.rms), 0)
  })

  /*
   * Only measured segments set the scale. A segment with no frames has a "peak" of digital silence,
   * and letting that into the range would stretch the span to ~100 dB and squash every real note
   * into the bottom of it.
   */
  const measured = peaks.filter(peak => peak !== null).map(toDb)
  const lo = Math.min(...measured)
  const hi = Math.max(...measured)
  const hasDynamics = measured.length > 0 && hi - lo >= FLAT_DYNAMICS_DB

  return segments.map((segment, index) => {
    const peak = peaks[index]
    const velocity =
      hasDynamics && peak !== null
        ? CAPTURE_VELOCITY_FLOOR + (1 - CAPTURE_VELOCITY_FLOOR) * ((toDb(peak) - lo) / (hi - lo))
        : FLAT_TAKE_VELOCITY

    return {
      midi: Math.min(127, Math.max(0, Math.round(segment.midi))),
      onsetMs: segment.startTime * 1000 - t0Ms,
      durationMs: (segment.endTime - segment.startTime) * 1000,
      velocity,
    }
  })
}
